import { z } from 'zod';

const objectIdString = z.string().regex(/^[a-fA-F0-9]{24}$/, 'Geçersiz kimlik');

export const AssignmentTemplateUsageProfileSchema = z.enum([
    'approval-chain',
    'audience',
    'hybrid',
]);

export const AssignmentTemplateApproverTypeSchema = z.enum([
    'user',
    'role',
    'unit-roles',
]);

export const AssignmentTemplateUnitTitleModeSchema = z.enum(['own', 'up-to-level', 'target-unit']);

/** Shared «kim» fields for chain steps and audience members (no CC / task policy). */
const AssignmentTemplateWhoMemberObjectSchema = z.object({
    order: z.number().int().min(1).optional(),
    name: z.string().max(500).optional(),
    approverType: AssignmentTemplateApproverTypeSchema,
    approvers: z.array(objectIdString).optional(),
    approverRole: objectIdString.optional(),
    approverRoles: z.array(objectIdString).optional(),
    approverUnit: objectIdString.optional(),
    approverUnits: z.array(objectIdString).optional(),
    unit: objectIdString.optional(),
    unitRole: objectIdString.optional(),
    approverUnitRoles: z.array(objectIdString).optional(),
    unitTargetingMode: AssignmentTemplateUnitTitleModeSchema.optional(),
    targetUnitLevel: z.number().optional(),
    targetUnit: objectIdString.optional(),
    directToTargetOnly: z.boolean().optional(),
});

function normalizeLegacyUnitTargetingMode(value) {
    if (!value || typeof value !== 'object' || Array.isArray(value)) return value;
    const member = value;
    return {
        ...member,
        unitTargetingMode: member.unitTargetingMode ?? member.titleMode,
    };
}

export const AssignmentTemplateWhoMemberSchema = z.preprocess(
    normalizeLegacyUnitTargetingMode,
    AssignmentTemplateWhoMemberObjectSchema,
);

export const AssignmentTemplateStepInputSchema = z.preprocess(
    normalizeLegacyUnitTargetingMode,
    AssignmentTemplateWhoMemberObjectSchema.extend({
        order: z.number().int().min(1),
        stepType: z.enum(['sequential', 'parallel']).optional(),
        requiredApprovals: z.number().int().min(1).optional(),
    }),
);

export const AssignmentTemplateAudienceMemberInputSchema = AssignmentTemplateWhoMemberSchema;

function validateUsageProfileShape(data, ctx) {
    const profile = data.usageProfile ?? 'approval-chain';
    const steps = data.steps ?? [];
    const members = data.audienceMembers ?? [];

    if (profile === 'approval-chain' && steps.length < 1) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Onay zinciri profili için en az bir adım gerekli',
            path: ['steps'],
        });
    }
    if (profile === 'audience' && steps.length < 1 && members.length < 1) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Hedef kitle profili için en az bir audience üyesi veya adım gerekli',
            path: ['audienceMembers'],
        });
    }
    if (profile === 'hybrid' && steps.length < 1 && members.length < 1) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Hibrit profil için adım veya hedef kitle üyesi gerekli',
            path: ['steps'],
        });
    }
}

/** Raw object shape — apply `.partial()` before `superRefine`. */
const AssignmentTemplateBodyObjectSchema = z.object({
    code: z.string().min(1).max(80).trim(),
    name: z.string().min(1).max(300).trim(),
    description: z.string().max(4000).optional(),
    contextKey: z.string().max(200).trim().optional(),
    usageProfile: AssignmentTemplateUsageProfileSchema.optional(),
    isActive: z.boolean().optional(),
    steps: z.array(AssignmentTemplateStepInputSchema).optional(),
    audienceMembers: z.array(AssignmentTemplateAudienceMemberInputSchema).optional(),
    customFields: z.record(z.unknown()).optional(),
});

export const AssignmentTemplateBodySchema =
    AssignmentTemplateBodyObjectSchema.superRefine(validateUsageProfileShape);

export const CreateAssignmentTemplateApiSchema = AssignmentTemplateBodySchema;

export const UpdateAssignmentTemplateApiSchema = AssignmentTemplateBodyObjectSchema.partial()
    .refine((data) => Object.keys(data).length > 0, { message: 'Güncellenecek alan yok' })
    .superRefine((data, ctx) => {
        if (data.usageProfile != null || data.steps != null || data.audienceMembers != null) {
            validateUsageProfileShape(
                {
                    usageProfile: data.usageProfile ?? 'approval-chain',
                    steps: data.steps ?? [],
                    audienceMembers: data.audienceMembers ?? [],
                },
                ctx,
            );
        }
    });

/** Workflow assigneeConfig when assigneeStrategy is assignment-template */
export const AssignmentTemplateAssigneeConfigSchema = z.object({
    assignmentTemplateId: objectIdString.optional(),
    resolutionMode: z.enum(['chain', 'audience']).optional(),
    includeStepsInAudience: z.boolean().optional(),
});
